// ─── Public Status Page — one entry on the incident timeline ─────────
//
// The dot is coloured by what the update MEANS, not by the incident's overall
// severity: the closing "Løst" entry is green even on an incident that was red
// the whole way down, which is exactly how a reader scans a timeline for "when
// did this end?".
//
// The body is operator-authored HTML and goes through <RichText>, never
// dangerouslySetInnerHTML — see ui/RichText.tsx for the allowlist.

import {
  STATUS_INCIDENT_STATUS_LABELS_DA,
  STATUS_UPDATE_KIND_LABELS_DA,
  type PublicStatusUpdate,
  type StatusComponentStatus,
  type StatusIncidentStatus,
} from '@pcp/shared';
import { da } from '../i18n/da';
import { fmtDateTime } from '../lib/format';
import { levelForStatus, STATUS_LEVELS, type StatusLevel } from '../lib/levels';
import { SIcon } from '../ui/SIcon';
import { RichText } from '../ui/RichText';
import { VisuallyHidden } from '../ui/VisuallyHidden';

export interface IncidentUpdateProps {
  update: PublicStatusUpdate;
  /** The parent incident's impact — the tint for the "still ongoing" entries. */
  impact: StatusComponentStatus;
}

/** Lifecycle status → the dot's colour. */
function levelForUpdate(status: StatusIncidentStatus, impact: StatusComponentStatus): StatusLevel {
  switch (status) {
    case 'resolved':
    case 'completed':
      return STATUS_LEVELS.available;
    case 'scheduled':
    case 'in_progress':
    case 'published':
      return STATUS_LEVELS.maintenance;
    default:
      // investigating / identified / monitoring — carry the incident's own colour.
      return levelForStatus(impact);
  }
}

export function IncidentUpdate({ update, impact }: IncidentUpdateProps) {
  const level = levelForUpdate(update.status, impact);
  // A ROOT-CAUSE entry is headed by what it IS, not by the status it carries:
  // the write-up posted after an incident closes is still 'resolved', so without
  // this it would read "Løst" — the same heading as the entry that actually
  // closed the incident, two rows above it. The marker still comes from the
  // status, so it stays green.
  const label = update.kind === 'root_cause'
    ? STATUS_UPDATE_KIND_LABELS_DA.root_cause
    : update.statusLabel || STATUS_INCIDENT_STATUS_LABELS_DA[update.status] || '';

  return (
    <article className="sp-update">
      {/* A solid dot, so the block tier — same colour the day grid paints for
          this status, which is what makes the two read as one system. */}
      <span className="sp-update-marker" style={{ background: level.fill }} />
      <div className="sp-update-head">
        {/* "Opdatering" is obvious from the timeline's shape but invisible to a
            screen reader walking the list, so it is announced, not painted. */}
        <VisuallyHidden>{`${da.incidentUpdate}: `}</VisuallyHidden>
        <span className="sp-update-status">{label}</span>
        <span className="sp-update-time">
          <SIcon name="clock" size={12} /> {fmtDateTime(update.occurredAt)}
        </span>
      </div>
      <RichText html={update.body} />
    </article>
  );
}
